import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Phone, MapPin, RefreshCw, Loader2, TrendingUp, ExternalLink } from 'lucide-react';

const STATUT_COLORS = {
  nouveau: '#9CA3AF', a_appeler: '#3B82F6', rappel_demande: '#F59E0B', injoignable: '#6B7280',
  interesse: '#10B981', en_negociation: '#8B5CF6', rdv_obtenu: '#F97316',
};

export default function TopCallsWidget({ onProspectClick, limit = 10 }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/ai/top-calls?limit=${limit}`);
      setCalls(data.top_calls || []);
    } catch {}
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading) return null;

  return (
    <Card data-testid="top-calls-widget" className="border-l-4 border-l-[#F97316]">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#F97316]" />
            Top {calls.length} appels recommandés aujourd'hui
          </CardTitle>
          <p className="text-[11px] text-[#71717A] mt-0.5">Priorisation IA selon urgence + intérêt</p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={refreshing} className="h-7 px-2" data-testid="top-calls-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="pt-0 space-y-1.5 max-h-[420px] overflow-y-auto">
        {calls.length === 0 && (
          <p className="text-xs text-[#71717A] p-3 text-center">Aucun prospect prioritaire à appeler pour le moment.</p>
        )}
        {calls.map((c, i) => (
          <div
            key={c.id}
            className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E7EB] hover:bg-[#FAFAFA] hover:border-[#F97316]/40 cursor-pointer transition-colors"
            onClick={() => onProspectClick?.(c.id)}
            data-testid={`top-call-${i}`}
          >
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
              style={{ background: i < 3 ? '#F97316' : '#F4F4F5', color: i < 3 ? 'white' : '#71717A' }}>
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-xs font-semibold truncate">{c.nom_magasin || c.enseigne}</span>
                <Badge variant="outline" className="text-[9px] h-3.5 px-1.5"
                  style={{ borderColor: STATUT_COLORS[c.statut_commercial] || '#9CA3AF', color: STATUT_COLORS[c.statut_commercial] || '#9CA3AF' }}>
                  {c.statut_commercial}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-[#71717A]">
                <MapPin className="w-2.5 h-2.5" /><span className="truncate">{c.ville}</span>
                {c.prenom_dirigeant && <span className="truncate">• {c.prenom_dirigeant} {c.nom_dirigeant}</span>}
              </div>
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                {c.reasons.slice(0, 2).map((r, j) => (
                  <span key={j} className="text-[9px] bg-[#FFF7ED] text-[#F97316] px-1.5 py-0.5 rounded">{r}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {c.telephone && c.telephone !== '?' && (
                <button
                  onClick={e => { e.stopPropagation(); onProspectClick?.(c.id); }}
                  className="flex items-center gap-1 h-7 px-2 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-[11px] font-medium"
                  title="Ouvrir la fiche"
                  data-testid={`top-call-open-${i}`}
                >
                  <ExternalLink className="w-3 h-3" />Ouvrir
                </button>
              )}
              <div className="text-right">
                <div className="flex items-center gap-0.5 text-xs font-bold" style={{ color: c.score >= 50 ? '#F97316' : '#71717A' }}>
                  <TrendingUp className="w-3 h-3" />{c.score}
                </div>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
